import { useCallback } from 'react';
import { STUDIO_THEMES } from 'fym-shared/src/music24/studioThemes';
import type { SpotifyCanvasSongResponse } from 'fym-shared/src/music24/spotifyCanvas';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { CanvasAction } from './spotifyCanvasRowRules';
import { useSpotifyCanvasConnect } from './useSpotifyCanvasConnect';
import { useSpotifyCanvasUpload } from './useSpotifyCanvasUpload';

/**
 * What each button of the Canvas row does (design spotify-canvas a2 note "Actions"): Connect and
 * Reconnect sign in to Spotify for Artists; Make a Canvas opens the Studio on the Canvas loop of the
 * song; Upload, Upload it, Try again and Replace it send the POST. Open on Spotify is a plain link.
 */
export function useSpotifyCanvasRowActions({
  accountId,
  data,
  refetch,
  hide,
}: {
  accountId: number;
  data: SpotifyCanvasSongResponse;
  refetch: () => void;
  hide: () => void;
}) {
  const router = useSMLRouter();
  const { catalogItemUuid } = data;
  const signIn = useSpotifyCanvasConnect({ accountId, onConnected: refetch });
  const upload = useSpotifyCanvasUpload({
    accountId,
    catalogItemUuid,
    termsAccepted: data.settings.termsAcceptedAt !== null,
    refetch,
    hide,
  });
  const { connect } = signIn;
  const { upload: startUpload } = upload;

  const run = useCallback(
    (action: CanvasAction) => {
      switch (action) {
        case 'connect':
        case 'reconnect':
          connect();
          return;
        case 'makeCanvas':
        case 'makeNewCanvas':
          router.push(SMLRoutes.promoteStudio, {
            catalogItemUuid,
            format: STUDIO_THEMES.canvasLoop.format,
            theme: 'canvasLoop',
          });
          return;
        case 'upload':
        case 'uploadIt':
        case 'retry':
        case 'replace':
          startUpload();
          break;
        case 'open':
          // A link of the row: the browser opens it.
          break;
      }
    },
    [catalogItemUuid, connect, router, startUpload]
  );

  return { run, signIn, upload, isBusy: signIn.isConnecting || upload.isUploading };
}
